/**
 * ============================================================================
 * FILE: useQueries.js
 * PATH: composables/queries/useQueries.js
 * LINES: 1334
 * ============================================================================
 * 
 * PURPOSE:
 * Centralized Vue Query composables for server state management. Provides
 * reactive data fetching hooks with automatic caching, deduplication, and
 * cache invalidation. Replaces manual caching and event-based refresh patterns.
 * 
 * ARCHITECTURE:
 * ┌─────────────────────────────────────────────────────────────────────┐
 * │                         Vue Query                                   │
 * │  ┌──────────────┐ ┌──────────────┐ ┌──────────────┐               │
 * │  │ useScenario  │ │ useNetwork   │ │ useScenarios │               │
 * │  │ Costs        │ │ Data         │ │ List         │               │
 * │  └──────┬───────┘ └──────┬───────┘ └──────┬───────┘               │
 * │         │                │                │                        │
 * │         └────────────────┼────────────────┘                        │
 * │                          ▼                                         │
 * │              ┌──────────────────────┐                              │
 * │              │   Query Client       │                              │
 * │              │   (cache + dedupe)   │                              │
 * │              └──────────┬───────────┘                              │
 * │                         │                                          │
 * └─────────────────────────┼──────────────────────────────────────────┘
 *                           ▼
 *                    Backend APIs
 * 
 * DOMAIN: Vue Query / Server State Management
 * LAYER: Composable (Data Fetching Layer)
 * 
 * ============================================================================
 * KEY BENEFITS:
 * ============================================================================
 * 
 * 1. Automatic cache invalidation via query keys
 * 2. No custom events needed - components auto-update when cache changes
 * 3. Deduplication - multiple components requesting same data = 1 API call
 * 4. Background refetching with stale-while-revalidate
 * 5. Built-in loading/error states
 * 
 * ============================================================================
 * EXPORTED FUNCTIONS & LINE NUMBERS:
 * ============================================================================
 * 
 * QUERY KEYS (Centralized):
 * - queryKeys object                      Lines 74-115
 *   Helper functions to generate consistent query keys
 * 
 * API FETCH FUNCTIONS:
 * - fetchScenarioCosts()                  Lines 125-140
 *   Fetches scenario costs with logging
 * 
 * - fetchNetworkData()                    Lines 145-159
 *   Fetches network data (facilities, lanes, customers) with logging
 * 
 * - fetchScenarios()                      Lines 164-169
 *   Fetches scenarios list
 * 
 * - fetchFacilitiesBreakdown()            Line 174
 *   Fetches facilities breakdown data
 * 
 * - fetchFacilityFlows()                  Line 179
 *   Fetches facility flow data
 * 
 * - fetchPurchaseMethodStats()            Line 184
 *   Fetches purchase method statistics
 * 
 * - fetchOptimizationConfig()             Line 189
 *   Fetches optimization configuration
 * 
 * - fetchSimulationConfig()                Line 194
 *   Fetches simulation configuration
 * 
 * QUERY COMPOSABLES (Reactive Hooks):
 * - useScenarioCosts(scenarioId, opts)   Lines 210-250
 *   Reactive hook for scenario costs with automatic caching
 * 
 * - useBaselineCosts(opts)                Lines 263-292
 *   Reactive hook for baseline scenario costs (scenario 1)
 * 
 * - useNetworkData(scenarioId, opts)      Lines 304-349
 *   Reactive hook for network data (facilities, lanes, customers)
 * 
 * - useScenarios(opts)                    Lines 350-384
 *   Reactive hook for scenarios list
 * 
 * - usePrebuiltScenarios(opts)            Lines 385-434
 *   Reactive hook for prebuilt scenarios
 * 
 * - useFacilitiesBreakdown(scenarioId)    Lines 435-474
 *   Reactive hook for facilities breakdown
 * 
 * - useFacilityFlows(facilityName, ...)   Lines 475-513
 *   Reactive hook for facility flow data
 * 
 * - usePurchaseMethodStats(scenarioId)    Lines 514-549
 *   Reactive hook for purchase method statistics
 * 
 * - useScenarioComparison(workspaceId)    Lines 550-582
 *   Reactive hook for scenario comparison data
 * 
 * - useScenarioFlowResults(scenarioId)    Lines 583-614
 *   Reactive hook for scenario flow results
 * 
 * - useOptimizationConfig(scenarioId)     Lines 615-648
 *   Reactive hook for optimization configuration
 * 
 * - useSimulationConfig(scenarioId)       Lines 649-683
 *   Reactive hook for simulation configuration
 * 
 * - useScenarioBundle(scenarioId)         Lines 684-733
 *   Reactive hook for scenario bundle (all data)
 * 
 * UTILITY FUNCTIONS:
 * - calculateDistance(lat1, lon1, ...)    Lines 734-757
 *   Calculates distance between two coordinates
 * 
 * MUTATION COMPOSABLES:
 * - useRunOptimization()                  Lines 758-804
 *   Mutation for running optimization
 * 
 * - useRunSimulation()                    Lines 805-849
 *   Mutation for running simulation
 * 
 * - useSaveOptimizationConfig()           Lines 850-875
 *   Mutation for saving optimization config
 * 
 * - useSaveSimulationConfig()             Lines 876-901
 *   Mutation for saving simulation config
 * 
 * - useDeleteScenarioData()               Lines 902-933
 *   Mutation for deleting scenario data
 * 
 * - useImportData()                       Lines 934-978
 *   Mutation for importing data
 * 
 * - useRunScenarios()                     Lines 979-1009
 *   Mutation for running multiple scenarios
 * 
 * - useBatchStatus(opts)                  Lines 1010-1029
 *   Query for batch operation status
 * 
 * - useUpdateScenarioName()              Lines 1030-1052
 *   Mutation for updating scenario name
 * 
 * - useResetAllStatuses()                Lines 1053-1075
 *   Mutation for resetting all batch statuses
 * 
 * - useCustomScenarios(opts)             Lines 1076-1096
 *   Query for custom scenarios
 * 
 * - useImportCustomScenario()            Lines 1097-1115
 *   Mutation for importing custom scenario
 * 
 * - useCreateCustomScenarioFromOptimized() Lines 1116-1138
 *   Mutation for creating custom scenario from optimized
 * 
 * - useRunCustomScenario()               Lines 1139-1157
 *   Mutation for running custom scenario
 * 
 * - useDeleteCustomScenario()            Lines 1158-1184
 *   Mutation for deleting custom scenario
 * 
 * QUERY CLIENT HELPERS:
 * - useAppQueryClient()                  Lines 1185-1202
 *   Returns query client instance
 * 
 * - useInvalidateScenario()              Lines 1203-1226
 *   Helper to invalidate all queries for a scenario
 * 
 * - usePrefetchScenario()                Lines 1227-1263
 *   Helper to prefetch scenario data
 * 
 * FORMATTING HELPERS:
 * - useFormattedStats(costs)             Lines 1264-1334
 *   Formats cost statistics for display
 * 
 * ============================================================================
 * DEPENDENCIES:
 * ============================================================================
 * 
 * External:
 * - vue (computed, unref, ref)
 * - @tanstack/vue-query (useQuery, useMutation, useQueryClient)
 * - axios
 * - @/stores/app (useAppStore)
 * - @/services/scenarios/scenarios.api (all API functions)
 * 
 * ============================================================================
 * USAGE EXAMPLES:
 * ============================================================================
 * 
 * // Fetch scenario costs
 * const { data: costs, isLoading, error } = useScenarioCosts(scenarioId)
 * 
 * // Fetch network data
 * const { data: networkData } = useNetworkData(scenarioId)
 * 
 * // Run optimization
 * const runOptimization = useRunOptimization()
 * await runOptimization.mutateAsync({ scenario_id: 1, ... })
 * 
 * // Invalidate cache after mutation
 * const queryClient = useAppQueryClient()
 * queryClient.invalidateQueries({ queryKey: ['scenarioCosts', scenarioId] })
 * 
 * ============================================================================
 * CACHE MANAGEMENT:
 * ============================================================================
 * 
 * - Query keys are centralized in queryKeys object
 * - Cache invalidation happens automatically after mutations
 * - staleTime: 30s for data queries (prevents re-fetch loops; cache invalidation forces refresh after mutations)
 * - staleTime: 60s for configuration data (less volatile)
 * - refetchOnWindowFocus disabled (cache invalidation handles freshness)
 * 
 * ============================================================================
 */

import { computed, unref, ref, toValue } from 'vue'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import axios from 'axios'
import { useAppStore } from '@/core/store'
import {
  getScenarioCosts,
  getNetworkData,
  getScenarios,
  getPrebuiltScenarios,
  getFacilitiesBreakdown,
  getFacilityFlows,
  getPurchaseMethodStats,
  getOptimizationConfig,
  getSimulationConfig,
  importCustomScenario,
  getBatchStatus,
  runScenarios,
  getScenarioComparison,
  getScenarioFlowResults,
  getScenarioBundle,
  updateScenarioName,
  resetAllStatuses,
  getCustomScenarios,
  createCustomScenarioFromOptimized,
  runCustomScenario,
  deleteCustomScenario
} from '@/features/scenarios/api/scenarios.api'
import { getHeatmapData } from '@/features/map-customization/api/presentation.api'
import { getFacilities } from '@/features/data-management/api/data.api'
import { distance } from '@/core/geo'
import { apiClient } from '@/core/api'

// ============================================================================
// QUERY KEYS - Centralized key management for cache invalidation
// ============================================================================

export { queryKeys, designResultKey } from './queryKeys'
import { queryKeys } from './queryKeys'
import { baselineCostsOptions, networkDataOptions, networkModeFor, scenarioCostsOptions } from './cardQueries'

// ============================================================================
// API FUNCTIONS - Re-exported from service layer for backward compatibility
// ============================================================================

/**
 * ✅ BEST PRACTICE: Re-export service functions for components that need direct access
 * Most components should use Vue Query hooks instead
 */
export const fetchScenarioCosts = async (scenarioId, resultType, workspaceId) => {
  console.log(`💰 [VueQuery] Fetching costs: scenario=${scenarioId}, type=${resultType}, ws=${workspaceId}`)
  
  const normalized = await getScenarioCosts(scenarioId, resultType, workspaceId)
  
  console.log(`✅ [VueQuery] Costs loaded: scenario=${scenarioId}, total_cost=${normalized.total_cost}`)
  
  return normalized
}

/**
 * ✅ BEST PRACTICE: Re-export service function with logging
 */
export const fetchNetworkData = async (scenarioId, mode, workspaceId) => {
  console.log(`🌐 [VueQuery] Fetching network: scenario=${scenarioId}, mode=${mode}, ws=${workspaceId}`)
  
  const data = await getNetworkData(scenarioId, mode, workspaceId)
  
  console.log(`✅ [VueQuery] Network loaded: scenario=${scenarioId}, facilities=${data.facilities?.length || 0}, lanes=${data.lanes?.length || 0}`)
  
  return data
}

/**
 * ✅ BEST PRACTICE: Use service function with logging
 */
const fetchScenarios = async (workspaceId) => {
  console.log(`📋 [VueQuery] Fetching scenarios for workspace ${workspaceId}`)
  const scenarios = await getScenarios(workspaceId)
  console.log(`✅ [VueQuery] Loaded ${scenarios.length} scenarios`)
  return scenarios
}

/**
 * ✅ BEST PRACTICE: Use service function
 */
const fetchFacilitiesBreakdown = getFacilitiesBreakdown

/**
 * ✅ BEST PRACTICE: Re-export service function for backward compatibility
 */
export const fetchFacilityFlows = getFacilityFlows

/**
 * ✅ BEST PRACTICE: Use service function
 */
const fetchPurchaseMethodStats = getPurchaseMethodStats

/**
 * ✅ BEST PRACTICE: Use service function for heatmap data
 */
const fetchHeatmapData = getHeatmapData

/**
 * ✅ BEST PRACTICE: Use service function for facilities
 */
const fetchFacilities = getFacilities

/**
 * ✅ BEST PRACTICE: Use service function
 */
const fetchOptimizationConfig = getOptimizationConfig

/**
 * ✅ BEST PRACTICE: Use service function
 */
const fetchSimulationConfig = getSimulationConfig

// ============================================================================
// QUERY COMPOSABLES - Reactive data fetching hooks
// ============================================================================

/**
 * Fetch scenario costs with automatic caching
 * 
 * @param {Ref<number>|number} scenarioId - Scenario ID (reactive or static)
 * @param {Object} options - Query options
 * @returns {Object} Query result with data, isLoading, error, refetch, etc.
 * 
 * Example:
 *   const { data: costs, isLoading } = useScenarioCosts(computed(() => appStore.currentScenarioId))
 */
export function useScenarioCosts(scenarioId, options = {}) {
  const appStore = useAppStore()

  const {
    resultType = computed(() => appStore.currentResultType || 'optimization'),
    workspaceId = computed(() => appStore.workspaceId || 0),
    enabled = true,
    ...queryOptions
  } = options

  // The key, the fetcher and every option come from the neutral module, so the
  // React cards (Phase 1I) address the same cache entry with the same
  // behaviour. Only the reactivity is Vue's.
  const shared = computed(() =>
    scenarioCostsOptions({
      scenarioId: unref(scenarioId),
      resultType: unref(resultType),
      workspaceId: unref(workspaceId),
    }),
  )

  return useQuery({
    ...shared.value,
    queryKey: computed(() => shared.value.queryKey),
    queryFn: () => shared.value.queryFn(),
    enabled: computed(() => unref(enabled) && shared.value.enabled),
    ...queryOptions
  })
}

/**
 * ✅ BEST PRACTICE: Fetch baseline costs (scenario 1) for comparison
 * The costs of the scenario a comparison is measured AGAINST.
 *
 * It used to ignore its caller and always fetch scenario 1: the baseline the
 * user picks in Presentation Mode only triggered a refetch of scenario 1, so
 * every delta was measured against a baseline they had not chosen. Pass the
 * chosen id — a ref, a getter or a number.
 *
 * `result_type` stays 'optimization': a baseline is what the network costs when
 * optimised, and comparing against a simulation run would answer a different
 * question.
 *
 * @param {Ref<number>|Function|number} baselineScenarioId - the scenario to compare against
 * @param {Object} options - Query options
 * @returns {Object} Query result with baseline costs
 */
export function useBaselineCosts(baselineScenarioId = 1, options = {}) {
  const appStore = useAppStore()

  const {
    workspaceId = computed(() => appStore.workspaceId || 0),
    enabled = true,
    ...queryOptions
  } = options

  const shared = computed(() =>
    baselineCostsOptions({
      baselineScenarioId: toValue(baselineScenarioId),
      workspaceId: unref(workspaceId),
    }),
  )

  return useQuery({
    ...shared.value,
    queryKey: computed(() => shared.value.queryKey),
    queryFn: () => shared.value.queryFn(),
    enabled: computed(() => unref(enabled) && shared.value.enabled),
    ...queryOptions
  })
}

/**
 * Fetch network data (facilities, lanes, customers) with automatic caching
 * 
 * @param {Ref<number>|number} scenarioId - Scenario ID (reactive or static)
 * @param {Object} options - Query options
 * @returns {Object} Query result
 * 
 * Example:
 *   const { data: network, isLoading } = useNetworkData(scenarioId, { mode: 'optimized' })
 */
export function useNetworkData(scenarioId, options = {}) {
  const appStore = useAppStore()

  const safeOptions = options && typeof options === 'object' ? options : {}
  const {
    mode = computed(() => networkModeFor(appStore.currentScenario)),
    workspaceId = computed(() => appStore.workspaceId || 0),
    enabled = true,
    ...queryOptions
  } = safeOptions

  // Key, fetcher and options from the neutral module — see cardQueries.ts.
  const shared = computed(() =>
    networkDataOptions({
      scenarioId: unref(scenarioId),
      mode: unref(mode),
      workspaceId: unref(workspaceId),
    }),
  )

  // `enabled` here has always accepted a boolean, a ref or a function.
  const isEnabled = computed(() => {
    if (!shared.value.enabled) return false
    if (typeof enabled === 'boolean') return enabled
    if (typeof enabled === 'function') return enabled()
    if (enabled && typeof enabled === 'object' && ('value' in enabled || 'get' in enabled)) return unref(enabled)
    return true
  })

  return useQuery({
    ...shared.value,
    queryKey: computed(() => shared.value.queryKey),
    queryFn: () => shared.value.queryFn(),
    enabled: isEnabled,
    ...queryOptions
  })
}

/**
 * Fetch scenarios list with automatic caching
 * 
 * @param {Object} options - Query options
 * @returns {Object} Query result
 * 
 * Example:
 *   const { data: scenarios, isLoading } = useScenarios()
 */
export function useScenarios(options = {}) {
  const appStore = useAppStore()
  
  const {
    workspaceId = computed(() => appStore.workspaceId || 0),
    enabled = true,
    ...queryOptions
  } = options
  
  return useQuery({
    queryKey: computed(() => queryKeys.scenarios(unref(workspaceId))),
    queryFn: () => fetchScenarios(unref(workspaceId)),
    enabled,
    staleTime: 60 * 1000, // Scenarios list is less volatile
    gcTime: 10 * 60 * 1000,
    ...queryOptions
  })
}

// ============================================================================
// PREBUILT SCENARIOS - With automatic polling for running scenarios
// ============================================================================

/**
 * ✅ BEST PRACTICE: Use service function
 */
const fetchPrebuiltScenarios = getPrebuiltScenarios

/**
 * ✅ BEST PRACTICE: Use Vue Query's refetchInterval for polling
 * Automatically polls while scenarios are running, stops when complete
 * 
 * @param {Object} options - Query options
 * @param {Ref<boolean>} options.enablePolling - Whether to enable auto-polling
 */
export function usePrebuiltScenarios(options = {}) {
  const appStore = useAppStore()
  
  const {
    workspaceId = computed(() => appStore.workspaceId || 0),
    enablePolling = ref(false),
    enabled = true,
    ...queryOptions
  } = options
  
  const query = useQuery({
    queryKey: computed(() => ['prebuiltScenarios', unref(workspaceId)]),
    queryFn: () => fetchPrebuiltScenarios(unref(workspaceId)),
    enabled: computed(() => unref(enabled) && unref(workspaceId) > 0),
    staleTime: 30 * 1000, // 30s — invalidation handles freshness after mutations
    refetchOnMount: true,
    // ✅ BEST PRACTICE: Use refetchInterval instead of setInterval
    // Only poll when enablePolling is true AND there are running scenarios
    refetchInterval: (data) => {
      if (!unref(enablePolling)) return false
      const hasRunning = data?.some(s => s.status === 'running')
      return hasRunning ? 2000 : false // Poll every 2s while running
    },
    ...queryOptions
  })
  
  // Computed for convenience
  const runningScenarios = computed(() => 
    (query.data.value || []).filter(s => s.status === 'running')
  )
  
  const hasRunningScenarios = computed(() => runningScenarios.value.length > 0)
  
  return {
    ...query,
    runningScenarios,
    hasRunningScenarios,
    enablePolling
  }
}

/**
 * Fetch facilities breakdown for a scenario
 * 🆕 COMPREHENSIVE CACHING: Accept resultType parameter
 * 
 * @param {Ref<number>|number} scenarioId - Scenario ID
 * @param {Object} options - Query options
 * @param {Ref<string>|string} options.resultType - 'optimization' or 'simulation' (default: from appStore)
 * @returns {Object} Query result
 */
export function useFacilitiesBreakdown(scenarioId, options = {}) {
  const appStore = useAppStore()
  
  const {
    resultType = computed(() => appStore.currentResultType || 'optimization'),
    workspaceId = computed(() => appStore.workspaceId || 0),
    enabled = true,
    ...queryOptions
  } = options
  
  return useQuery({
    queryKey: computed(() => queryKeys.facilitiesBreakdown(
      unref(scenarioId),
      unref(resultType),
      unref(workspaceId)
    )),
    queryFn: () => fetchFacilitiesBreakdown(
      unref(scenarioId),
      unref(workspaceId),
      unref(resultType)
    ),
    enabled: computed(() => {
      const sid = unref(scenarioId)
      return unref(enabled) && sid != null && sid > 0
    }),
    // ✅ P0 FIX: 30s staleTime prevents re-fetch loops; invalidation handles freshness
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    ...queryOptions
  })
}

/**
 * Fetch facility flows (inbound/outbound)
 * 
 * @param {Ref<string>|string} facilityName - Facility name
 * @param {Ref<number>|number} scenarioId - Scenario ID
 * @param {Object} options - Query options
 * @returns {Object} Query result
 */
export function useFacilityFlows(facilityName, scenarioId, options = {}) {
  const appStore = useAppStore()
  
  const {
    workspaceId = computed(() => appStore.workspaceId || 0),
    enabled = true,
    ...queryOptions
  } = options
  
  return useQuery({
    queryKey: computed(() => queryKeys.facilityFlows(
      unref(facilityName),
      unref(scenarioId),
      unref(workspaceId)
    )),
    queryFn: () => fetchFacilityFlows(
      unref(facilityName),
      unref(scenarioId),
      unref(workspaceId)
    ),
    enabled: computed(() => {
      const fname = unref(facilityName)
      const sid = unref(scenarioId)
      return unref(enabled) && fname && sid != null && sid > 0
    }),
    // ✅ P0 FIX: 30s staleTime prevents re-fetch loops; invalidation handles freshness
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    ...queryOptions
  })
}

/**
 * Fetch purchase method statistics
 * 
 * @param {Ref<number>|number} scenarioId - Scenario ID
 * @param {Object} options - Query options
 * @returns {Object} Query result
 */
export function usePurchaseMethodStats(scenarioId, options = {}) {
  const appStore = useAppStore()
  
  const {
    workspaceId = computed(() => appStore.workspaceId || 0),
    enabled = true,
    ...queryOptions
  } = options
  
  return useQuery({
    queryKey: computed(() => queryKeys.purchaseMethodStats(
      unref(scenarioId),
      unref(workspaceId)
    )),
    queryFn: () => fetchPurchaseMethodStats(
      unref(scenarioId),
      unref(workspaceId)
    ),
    enabled: computed(() => {
      const sid = unref(scenarioId)
      return unref(enabled) && sid != null && sid > 0
    }),
    // ✅ P0 FIX: 30s staleTime prevents re-fetch loops; invalidation handles freshness
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    ...queryOptions
  })
}

/**
 * Fetch scenario comparison data
 * 
 * @param {Ref<number>|number} workspaceId - Workspace ID
 * @param {Object} options - Query options
 * @returns {Object} Query result with comparison data
 */
export function useScenarioComparison(workspaceId, options = {}) {
  const appStore = useAppStore()
  
  const {
    effectiveWorkspaceId = computed(() => 
      workspaceId !== null && workspaceId !== undefined 
        ? (typeof workspaceId === 'object' ? workspaceId.value : workspaceId)
        : (appStore.workspaceId || 0)
    ),
    enabled = true,
    ...queryOptions
  } = options
  
  return useQuery({
    queryKey: computed(() => ['scenarioComparison', unref(effectiveWorkspaceId)]),
    queryFn: () => getScenarioComparison(unref(effectiveWorkspaceId)),
    enabled: computed(() => {
      const wsId = unref(effectiveWorkspaceId)
      return unref(enabled) && wsId > 0
    }),
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: false,
    ...queryOptions
  })
}

/**
 * Fetch scenario flow results
 * 
 * @param {Ref<number>|number} scenarioId - Scenario ID
 * @param {Object} options - Query options
 * @returns {Object} Query result with flow results
 */
export function useScenarioFlowResults(scenarioId, options = {}) {
  const appStore = useAppStore()
  
  const {
    workspaceId = computed(() => appStore.workspaceId || 0),
    enabled = true,
    ...queryOptions
  } = options
  
  return useQuery({
    queryKey: computed(() => ['scenarioFlowResults', unref(scenarioId), unref(workspaceId)]),
    queryFn: () => getScenarioFlowResults(
      unref(scenarioId),
      unref(workspaceId)
    ),
    enabled: computed(() => {
      const sid = unref(scenarioId)
      return unref(enabled) && sid != null && sid > 0
    }),
    staleTime: 30 * 1000, // 30s — invalidation handles freshness after mutations
    refetchOnWindowFocus: false,
    ...queryOptions
  })
}

/**
 * Fetch optimization configuration
 * 
 * @param {Ref<number>|number} scenarioId - Scenario ID
 * @param {Object} options - Query options
 * @returns {Object} Query result
 */
export function useOptimizationConfig(scenarioId, options = {}) {
  const appStore = useAppStore()
  
  const {
    workspaceId = computed(() => appStore.workspaceId || 0),
    enabled = true,
    ...queryOptions
  } = options
  
  return useQuery({
    queryKey: computed(() => queryKeys.optimizationConfig(
      unref(scenarioId),
      unref(workspaceId)
    )),
    queryFn: () => fetchOptimizationConfig(
      unref(scenarioId),
      unref(workspaceId)
    ),
    enabled: computed(() => {
      const sid = unref(scenarioId)
      return unref(enabled) && sid != null && sid > 0
    }),
    staleTime: 60 * 1000,
    ...queryOptions
  })
}

/**
 * Fetch simulation/uncertainty configuration
 * 
 * @param {Ref<number>|number} scenarioId - Scenario ID
 * @param {Object} options - Query options
 * @returns {Object} Query result
 */
export function useSimulationConfig(scenarioId, options = {}) {
  const appStore = useAppStore()
  
  const {
    workspaceId = computed(() => appStore.workspaceId || 0),
    enabled = true,
    ...queryOptions
  } = options
  
  return useQuery({
    queryKey: computed(() => queryKeys.simulationConfig(
      unref(scenarioId),
      unref(workspaceId)
    )),
    queryFn: () => fetchSimulationConfig(
      unref(scenarioId),
      unref(workspaceId)
    ),
    enabled: computed(() => {
      const sid = unref(scenarioId)
      return unref(enabled) && sid != null && sid > 0
    }),
    staleTime: 60 * 1000,
    ...queryOptions
  })
}

/**
 * Fetch scenario bundle (complete scenario data - facilities, lanes, costs, flows)
 * This replaces useModalDataCache for Vue Query-based caching
 * 
 * @param {Ref<number>|number} scenarioId - Scenario ID
 * @param {Object} options - Query options
 * @returns {Object} Query result with bundle data
 */
export function useScenarioBundle(scenarioId, options = {}) {
  const appStore = useAppStore()
  
  const {
    workspaceId = computed(() => appStore.workspaceId || 0),
    enabled = true,
    ...queryOptions
  } = options
  
  return useQuery({
    queryKey: computed(() => queryKeys.scenarioBundle(
      unref(scenarioId),
      unref(workspaceId)
    )),
    queryFn: async () => {
      const bundle = await getScenarioBundle(
        unref(scenarioId),
        unref(workspaceId)
      )
      
      // Calculate missing distances for lanes (same logic as useModalDataCache)
      if (bundle.lanes && bundle.lanes.length > 0) {
        bundle.lanes = bundle.lanes.map(lane => {
          if ((!lane.distance_km || lane.distance_km === 0) && 
              lane.origin_lat && lane.origin_lng && lane.dest_lat && lane.dest_lng) {
            const calculatedDistance = calculateDistance(
              lane.origin_lat, lane.origin_lng, 
              lane.dest_lat, lane.dest_lng
            )
            return { ...lane, distance_km: calculatedDistance }
          }
          return lane
        })
      }
      
      return bundle
    },
    enabled: computed(() => {
      const sid = unref(scenarioId)
      return unref(enabled) && sid != null && sid > 0
    }),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    ...queryOptions
  })
}

/** Kilometres, as this file's callers have always meant. See core/geo.ts. */
function calculateDistance(lat1, lon1, lat2, lon2) {
  return distance(lat1, lon1, lat2, lon2, 'km')
}

// ============================================================================
// MUTATION COMPOSABLES - For modifying server state
// ============================================================================

/**
 * Run optimization mutation
 * Automatically invalidates relevant caches on success
 * 
 * Example:
 *   const { mutateAsync: runOptimization, isPending } = useRunOptimization()
 *   await runOptimization({ scenario_id: 1 })
 */
export function useRunOptimization() {
  const queryClient = useQueryClient()
  const appStore = useAppStore()
  
  return useMutation({
    mutationFn: async (params) => {
      console.log('🚀 [VueQuery] Running optimization...', params)
      
      const response = await apiClient.post('/optimization/run', {
        scenario_id: params.scenario_id,
        workspace_id: params.workspace_id || appStore.workspaceId || 0,
        use_baseline: params.use_baseline !== false,
        objective: params.objective,
        facilityConfig: params.facilityConfig,
        fixedN: params.fixedN,
        multipliers: params.multipliers,
        additionalOptions: params.additionalOptions
      }, {
        timeout: 300000 // 5 minutes
      })
      
      return response.data
    },
    // ✅ OPTIMISTIC UPDATE: Update UI immediately while optimization runs
    onMutate: async (variables) => {
      console.log('⚡ [VueQuery] Optimistic update: Setting loading state...')
      
      const scenarioId = variables.scenario_id || appStore.currentScenarioId
      const workspaceId = variables.workspace_id || appStore.workspaceId || 0
      const costsKey = queryKeys.scenarioCosts(scenarioId, 'optimization', workspaceId)
      
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: costsKey })
      
      // Snapshot the previous value
      const previousCosts = queryClient.getQueryData(costsKey)
      
      // Optimistically update to show loading state
      queryClient.setQueryData(costsKey, (old) => ({
        ...old,
        isOptimizing: true,
        total_cost: old?.total_cost || 0,
        // Keep existing structure but mark as optimizing
      }))
      
      console.log('⚡ [VueQuery] Optimistic update applied - UI shows loading state')
      
      // Return context with previous value for rollback
      return { previousCosts, costsKey }
    },
    onSuccess: async (data, variables, context) => {
      console.log('✅ [VueQuery] Optimization completed')
      console.log('✅ [VueQuery] Optimization result data:', data)

      const scenarioId = variables.scenario_id || appStore.currentScenarioId
      const workspaceId = variables.workspace_id || appStore.workspaceId || 0

      // Set result type to optimization
      appStore.setCurrentResultType('optimization')

      // The run's response is deliberately NOT written into the costs cache.
      //
      // It used to be, "so components see the correct data immediately" — but it
      // is not the correct data, it is a different shape. `getScenarioCosts`
      // normalizes /scenarios/{id}/results into the one shape every reader of
      // this key expects; the POST response carries `fulfillment_rate` and none
      // of `service_level`, `open_dcs`, `demand_fulfilled`, `units_processed`.
      // `stats.ts` reads those as `|| 0`, so writing this shape here displayed
      // confident zeros — the Service Level card showed "0.0%" after a run that
      // reported 100% fulfilment.
      //
      // The invalidation below now refetches costs through the normalizer.
      console.log('🔄 [VueQuery] Invalidating related caches (costs included)...')

      // Import centralized cache invalidation function
      const { invalidateAfterOptimizationRun } = await import('./useCacheInvalidation')
      await invalidateAfterOptimizationRun(queryClient, scenarioId)

      console.log('✅ [VueQuery] Related caches invalidated, costs refetched through the normalizer')
    },
    onError: (error, variables, context) => {
      console.error('❌ [VueQuery] Optimization failed:', error)
      
      // ✅ OPTIMISTIC UPDATE: Rollback on error
      if (context?.previousCosts !== undefined && context?.costsKey) {
        console.log('🔄 [VueQuery] Rolling back optimistic update...')
        queryClient.setQueryData(context.costsKey, context.previousCosts)
        console.log('✅ [VueQuery] Optimistic update rolled back')
      }
    }
  })
}

/**
 * Run Monte Carlo simulation mutation
 * Automatically invalidates relevant caches on success
 */
export function useRunSimulation() {
  const queryClient = useQueryClient()
  const appStore = useAppStore()
  
  return useMutation({
    mutationFn: async (params) => {
      console.log('🎲 [VueQuery] Running simulation...', params)
      
      const response = await apiClient.post('/simulation/run', {
        scenario_id: params.scenario_id,
        workspace_id: params.workspace_id || appStore.workspaceId || 0,
        iterations: params.iterations || 100,
        uncertainty_config: params.uncertainty_config
      }, {
        timeout: 600000 // 10 minutes for simulation
      })
      
      return response.data
    },
    // ✅ OPTIMISTIC UPDATE: Show loading state immediately
    onMutate: async (variables) => {
      console.log('⚡ [VueQuery] Optimistic update: Setting simulation loading state...')
      
      const scenarioId = variables.scenario_id || appStore.currentScenarioId
      const workspaceId = variables.workspace_id || appStore.workspaceId || 0
      const costsKey = queryKeys.scenarioCosts(scenarioId, 'simulation', workspaceId)
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: costsKey })
      
      // Snapshot the previous value
      const previousCosts = queryClient.getQueryData(costsKey)
      
      // Optimistically update to show simulation in progress
      queryClient.setQueryData(costsKey, (old) => ({
        ...old,
        isSimulating: true,
        total_cost: old?.total_cost || 0,
        // Keep existing structure but mark as simulating
      }))
      
      console.log('⚡ [VueQuery] Optimistic update applied - UI shows simulation loading state')
      
      // Return context for rollback
      return { previousCosts, costsKey }
    },
    onSuccess: async (data, variables, context) => {
      console.log('✅ [VueQuery] Simulation completed, invalidating caches...')
      
      const scenarioId = variables.scenario_id
      
      // ✅ CENTRALIZED: Use centralized cache invalidation
      const { useCacheInvalidation } = await import('./useCacheInvalidation')
      const { invalidateAfterSimulation } = useCacheInvalidation()
      await invalidateAfterSimulation(scenarioId)
      
      console.log(`✅ [VueQuery] Simulation caches invalidated for scenario ${scenarioId}`)
    },
    onError: (error, variables, context) => {
      console.error('❌ [VueQuery] Simulation failed:', error)
      
      // ✅ OPTIMISTIC UPDATE: Rollback on error
      if (context?.previousCosts !== undefined && context?.costsKey) {
        console.log('🔄 [VueQuery] Rolling back optimistic update...')
        queryClient.setQueryData(context.costsKey, context.previousCosts)
        console.log('✅ [VueQuery] Optimistic update rolled back')
      }
    }
  })
}

/**
 * ✅ NOTE: useRunScenarios is defined below (line 889) using service layer
 * This duplicate has been removed to fix build error
 */

/**
 * Save optimization configuration mutation
 */
export function useSaveOptimizationConfig() {
  const appStore = useAppStore()
  
  return useMutation({
    mutationFn: async (config) => {
      console.log('💾 [VueQuery] Saving optimization config...', config)
      
      const response = await apiClient.post('/optimization/config/save', {
        ...config,
        workspace_id: config.workspace_id || appStore.workspaceId || 0
      })
      
      return response.data
    },
    onSuccess: async (data, variables) => {
      // ✅ CENTRALIZED: Use centralized cache invalidation
      const { useCacheInvalidation } = await import('./useCacheInvalidation')
      const { invalidateScenario } = useCacheInvalidation()
      await invalidateScenario(variables.scenario_id)
    }
  })
}

/**
 * Save simulation configuration mutation
 */
export function useSaveSimulationConfig() {
  const appStore = useAppStore()
  
  return useMutation({
    mutationFn: async (config) => {
      console.log('💾 [VueQuery] Saving simulation config...', config)
      
      const response = await apiClient.post('/simulation/config/save', {
        ...config,
        workspace_id: config.workspace_id || appStore.workspaceId || 0
      })
      
      return response.data
    },
    onSuccess: async (data, variables) => {
      // ✅ CENTRALIZED: Use centralized cache invalidation
      const { useCacheInvalidation } = await import('./useCacheInvalidation')
      const { invalidateScenario } = useCacheInvalidation()
      await invalidateScenario(variables.scenario_id)
    }
  })
}

/**
 * Delete scenario data mutation
 */
export function useDeleteScenarioData() {
  const appStore = useAppStore()
  
  return useMutation({
    mutationFn: async (params) => {
      console.log('🗑️ [VueQuery] Deleting scenario data...', params)
      
      const response = await apiClient.post('/data/clear-scenario-data', {
        scenario_id: params.scenario_id,
        scenario_label: params.scenario_label,
        workspace_id: params.workspace_id || appStore.workspaceId || 0
      })
      
      return response.data
    },
    onSuccess: async (data, variables) => {
      console.log('✅ [VueQuery] Data deleted, invalidating all caches...')
      
      const scenarioId = variables.scenario_id
      const workspaceId = variables.workspace_id || appStore.workspaceId || 0
      
      // ✅ CENTRALIZED: Use centralized cache invalidation
      const { useCacheInvalidation } = await import('./useCacheInvalidation')
      const { invalidateAfterDataChange } = useCacheInvalidation()
      await invalidateAfterDataChange(scenarioId, workspaceId)
    }
  })
}

/**
 * Import data mutation
 */
export function useImportData() {
  const appStore = useAppStore()
  
  return useMutation({
    mutationFn: async (params) => {
      console.log('📥 [VueQuery] Importing data...', params)
      
      const formData = new FormData()
      formData.append('file', params.file)
      formData.append('workspace_id', params.workspace_id || appStore.workspaceId || 0)
      
      if (params.scenario_id) {
        formData.append('scenario_id', params.scenario_id)
      }
      
      const response = await axios.post(params.endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 900000 // 15 minutes for large imports
      })
      
      return response.data
    },
    onSuccess: async (data, variables) => {
      console.log('✅ [VueQuery] Import completed, invalidating all caches...')
      
      const workspaceId = variables.workspace_id || appStore.workspaceId || 0
      const scenarioId = variables.scenario_id || null
      
      // ✅ CENTRALIZED: Use centralized cache invalidation
      const { useCacheInvalidation } = await import('./useCacheInvalidation')
      const { invalidateAfterDataChange } = useCacheInvalidation()
      await invalidateAfterDataChange(scenarioId, workspaceId)
    }
  })
}

// ============================================================================
// SCENARIO OPERATIONS MUTATIONS
// ============================================================================

/**
 * ✅ BEST PRACTICE: Run scenarios mutation
 * Use this instead of direct axios.post in components
 * Uses service layer function runScenarios()
 */
export function useRunScenarios() {
  const appStore = useAppStore()
  
  return useMutation({
    mutationFn: async (params) => {
      console.log(`🚀 [VueQuery] Running scenarios:`, params.scenarioIds?.length ? params.scenarioIds : 'ALL')
      
      return await runScenarios({
      ...params,
      workspaceId: params.workspaceId || appStore.workspaceId || 0
    })
    },
    onSuccess: async () => {
      console.log('✅ [VueQuery] Scenarios started, invalidating list...')
      
      const workspaceId = appStore.workspaceId || 0
      
      // ✅ CENTRALIZED: Use centralized cache invalidation
      const { useCacheInvalidation } = await import('./useCacheInvalidation')
      const { invalidateScenariosList } = useCacheInvalidation()
      await invalidateScenariosList(workspaceId)
    },
    onError: (error) => {
      console.error('❌ [VueQuery] Failed to start scenarios:', error)
    }
  })
}

/**
 * ✅ BEST PRACTICE: Batch status query with polling
 */
export function useBatchStatus(options = {}) {
  const {
    enabled = true,
    refetchInterval = false,
    ...queryOptions
  } = options
  
  return useQuery({
    queryKey: ['batchStatus'],
    queryFn: getBatchStatus,
    enabled,
    staleTime: 0,
    refetchInterval,
    ...queryOptions
  })
}

/**
 * ✅ BEST PRACTICE: Update scenario name mutation
 */
export function useUpdateScenarioName() {
  const appStore = useAppStore()
  
  // ✅ FIX: Initialize cache invalidation at setup time (Vue context available)
  // Cannot call composables inside async onSuccess callbacks
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ scenarioId, name }) => updateScenarioName(
      scenarioId,
      name,
      appStore.workspaceId || 0
    ),
    onSuccess: async () => {
      const workspaceId = appStore.workspaceId || 0
      
      // Invalidate prebuilt scenarios cache so names refresh
      await queryClient.invalidateQueries({ queryKey: ['prebuiltScenarios'] })
      await queryClient.invalidateQueries({ queryKey: ['scenariosStatus', workspaceId] })
    }
  })
}

/**
 * ✅ BEST PRACTICE: Reset all statuses mutation
 */
export function useResetAllStatuses() {
  const appStore = useAppStore()
  
  return useMutation({
    mutationFn: () => resetAllStatuses(appStore.workspaceId || 0),
    onSuccess: async () => {
      const workspaceId = appStore.workspaceId || 0
      
      // ✅ CENTRALIZED: Use centralized cache invalidation
      const { useCacheInvalidation } = await import('./useCacheInvalidation')
      const { invalidateScenariosList } = useCacheInvalidation()
      await invalidateScenariosList(workspaceId)
    }
  })
}

// ============================================================================
// CUSTOM SCENARIOS
// ============================================================================

/**
 * ✅ BEST PRACTICE: Get custom scenarios query
 */
export function useCustomScenarios(options = {}) {
  const appStore = useAppStore()
  
  const {
    workspaceId = computed(() => appStore.workspaceId || 0),
    enabled = true,
    ...queryOptions
  } = options
  
  return useQuery({
    queryKey: computed(() => ['customScenarios', unref(workspaceId)]),
    queryFn: () => getCustomScenarios(unref(workspaceId)),
    enabled: computed(() => unref(enabled) && unref(workspaceId) > 0),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    ...queryOptions
  })
}

/**
 * ✅ BEST PRACTICE: Import custom scenario mutation
 */
export function useImportCustomScenario() {
  const appStore = useAppStore()
  
  return useMutation({
    mutationFn: importCustomScenario,
    onSuccess: async () => {
      const workspaceId = appStore.workspaceId || 0
      
      // ✅ CENTRALIZED: Use centralized cache invalidation
      const { useCacheInvalidation } = await import('./useCacheInvalidation')
      const { invalidateCustomScenarios } = useCacheInvalidation()
      await invalidateCustomScenarios(workspaceId)
    }
  })
}

/**
 * ✅ BEST PRACTICE: Create custom scenario from optimized mutation
 */
export function useCreateCustomScenarioFromOptimized() {
  const appStore = useAppStore()
  
  return useMutation({
    mutationFn: ({ name, description }) => createCustomScenarioFromOptimized({
      name,
      description,
      workspaceId: appStore.workspaceId || 0
    }),
    onSuccess: async () => {
      const workspaceId = appStore.workspaceId || 0
      
      // ✅ CENTRALIZED: Use centralized cache invalidation
      const { useCacheInvalidation } = await import('./useCacheInvalidation')
      const { invalidateCustomScenarios } = useCacheInvalidation()
      await invalidateCustomScenarios(workspaceId)
    }
  })
}

/**
 * ✅ BEST PRACTICE: Run custom scenario mutation
 */
export function useRunCustomScenario() {
  const appStore = useAppStore()
  
  return useMutation({
    mutationFn: (scenarioId) => runCustomScenario(scenarioId, appStore.workspaceId || 0),
    onSuccess: async () => {
      const workspaceId = appStore.workspaceId || 0
      
      // ✅ CENTRALIZED: Use centralized cache invalidation
      const { useCacheInvalidation } = await import('./useCacheInvalidation')
      const { invalidateCustomScenarios } = useCacheInvalidation()
      await invalidateCustomScenarios(workspaceId)
    }
  })
}

/**
 * ✅ BEST PRACTICE: Delete custom scenario mutation
 */
export function useDeleteCustomScenario() {
  const appStore = useAppStore()
  
  return useMutation({
    mutationFn: (scenarioId) => deleteCustomScenario(scenarioId, appStore.workspaceId || 0),
    onSuccess: async () => {
      const workspaceId = appStore.workspaceId || 0
      
      // ✅ CENTRALIZED: Use centralized cache invalidation
      const { useCacheInvalidation } = await import('./useCacheInvalidation')
      const { invalidateCustomScenarios } = useCacheInvalidation()
      await invalidateCustomScenarios(workspaceId)
    }
  })
}

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Get the query client for manual cache operations
 * 
 * Example:
 *   const queryClient = useAppQueryClient()
 *   queryClient.invalidateQueries({ queryKey: ['scenarioCosts', 1] })
 */
export function useAppQueryClient() {
  return useQueryClient()
}

/**
 * ⚠️ DEPRECATED: Use useCacheInvalidation() instead
 * 
 * This function is kept for backward compatibility but delegates to useCacheInvalidation
 * 
 * @deprecated Use useCacheInvalidation() from './useCacheInvalidation' instead
 * 
 * Example:
 *   // ❌ OLD (deprecated):
 *   const { invalidateScenario } = useInvalidateScenario()
 *   
 *   // ✅ NEW (use this):
 *   const { invalidateScenario } = useCacheInvalidation()
 */
export function useInvalidateScenario() {
  console.warn('⚠️ [DEPRECATED] useInvalidateScenario() is deprecated. Use useCacheInvalidation() instead.')
  
  // Delegate to centralized composable
  try {
    const { useCacheInvalidation } = require('./useCacheInvalidation')
    return useCacheInvalidation()
  } catch (e) {
    console.error('Failed to import useCacheInvalidation:', e)
    // Return minimal fallback
    return {
      invalidateScenario: async () => {},
      invalidateAll: async () => {}
    }
  }
}

/**
 * Prefetch data for a scenario (for optimistic switching)
 * 
 * Example:
 *   const { prefetchScenario } = usePrefetchScenario()
 *   await prefetchScenario(2) // Prefetch before switching to scenario 2
 */
export function usePrefetchScenario() {
  const queryClient = useQueryClient()
  const appStore = useAppStore()
  
  const prefetchScenario = async (scenarioId, mode = 'optimized') => {
    const workspaceId = appStore.workspaceId || 0
    const resultType = appStore.currentResultType || 'optimization'
    
    console.log(`⚡ [VueQuery] Prefetching data for scenario ${scenarioId}`)
    
    await Promise.all([
      queryClient.prefetchQuery({
        queryKey: queryKeys.scenarioCosts(scenarioId, resultType, workspaceId),
        queryFn: () => fetchScenarioCosts(scenarioId, resultType, workspaceId)
      }),
      queryClient.prefetchQuery({
        queryKey: queryKeys.networkData(scenarioId, mode, workspaceId),
        queryFn: () => fetchNetworkData(scenarioId, mode, workspaceId)
      })
    ])
    
    console.log(`✅ [VueQuery] Prefetch complete for scenario ${scenarioId}`)
  }
  
  return { prefetchScenario }
}

// ============================================================================
// COMPUTED HELPERS - Derived data from queries
// ============================================================================

/**
 * Get formatted statistics from costs data
 * 
 * @param {Object} costs - Costs data from useScenarioCosts
 * @returns {Object} Formatted statistics
 */
export function useFormattedStats(costs) {
  return computed(() => {
    const data = unref(costs) || {}
    
    const formatCurrency = (value) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value || 0)
    }
    
    const formatCompact = (value) => {
      const numValue = value || 0
      if (numValue >= 1000000) {
        return '$' + (numValue / 1000000).toFixed(1) + 'M'
      } else if (numValue >= 1000) {
        return '$' + (numValue / 1000).toFixed(1) + 'K'
      }
      return formatCurrency(numValue)
    }
    
    return {
      totalCost: {
        raw: data.total_cost || 0,
        formatted: formatCurrency(data.total_cost || 0),
        compact: formatCompact(data.total_cost || 0)
      },
      fixedCost: {
        raw: data.fixed_cost || 0,
        formatted: formatCurrency(data.fixed_cost || 0),
        compact: formatCompact(data.fixed_cost || 0)
      },
      variableCost: {
        raw: data.variable_cost || 0,
        formatted: formatCurrency(data.variable_cost || 0),
        compact: formatCompact(data.variable_cost || 0)
      },
      shippingCost: {
        raw: data.shipping_cost || 0,
        formatted: formatCurrency(data.shipping_cost || 0),
        compact: formatCompact(data.shipping_cost || 0)
      },
      serviceLevel: {
        raw: data.service_level || 0,
        formatted: (data.service_level || 0).toFixed(1) + '%',
        color: (data.service_level || 0) >= 95 ? 'success' 
          : (data.service_level || 0) >= 85 ? 'warning' : 'error'
      },
      totalDemand: {
        raw: data.total_demand || 0,
        formatted: (data.total_demand || 0).toLocaleString()
      },
      demandFulfilled: {
        raw: data.demand_fulfilled || 0,
        formatted: (data.demand_fulfilled || 0).toLocaleString()
      },
      fulfillmentPercentage: {
        raw: data.fulfillment_percentage || 0,
        formatted: (data.fulfillment_percentage || 0).toFixed(1) + '%'
      }
    }
  })
}

/**
 * Fetch heatmap data with automatic caching
 * 
 * @param {Ref<number>|number} scenarioId - Scenario ID (reactive or static)
 * @param {Object} options - Query options
 * @returns {Object} Query result with heatmap data
 * 
 * Example:
 *   const { data: heatmapData, isLoading } = useHeatmapData(scenarioId, { mode: 'service' })
 */
export function useHeatmapData(scenarioId, options = {}) {
  const appStore = useAppStore()
  
  const {
    mode = 'service',
    workspaceId = computed(() => appStore.workspaceId || 0),
    enabled = true,
    ...queryOptions
  } = options
  
  return useQuery({
    queryKey: computed(() => queryKeys.heatmapData(
      unref(scenarioId),
      unref(mode),
      unref(workspaceId)
    )),
    queryFn: () => fetchHeatmapData(
      unref(scenarioId),
      unref(mode),
      unref(workspaceId)
    ),
    enabled: computed(() => {
      const sid = unref(scenarioId)
      return unref(enabled) && sid != null && sid > 0
    }),
    staleTime: 30 * 1000, // 30 seconds - heatmap data doesn't change often
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false, // Heatmap is not critical data
    ...queryOptions
  })
}

/**
 * Fetch facilities list with automatic caching
 * 
 * @param {Object} options - Query options
 * @returns {Object} Query result with facilities list
 * 
 * Example:
 *   const { data: facilities, isLoading } = useFacilities()
 */
export function useFacilities(options = {}) {
  const appStore = useAppStore()
  
  const {
    workspaceId = computed(() => appStore.workspaceId || 0),
    enabled = true,
    ...queryOptions
  } = options
  
  return useQuery({
    queryKey: computed(() => queryKeys.facilities(unref(workspaceId))),
    queryFn: () => fetchFacilities(unref(workspaceId)),
    enabled: computed(() => unref(enabled) && unref(workspaceId) > 0),
    staleTime: 60 * 1000, // 1 minute - facilities don't change often
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    ...queryOptions
  })
}






